import axios from '@fuse/utils/axios';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup'
import { makeStyles } from '@material-ui/core';
import React, { useState, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import useToast from '@fuse/hooks/useToast';
import {
    Box, Button, Icon,
    TextField, Typography, Grid, CircularProgress, Autocomplete, Paper,
    DialogTitle, Dialog, DialogContent, DialogActions,
    Tooltip
} from '@mui/material';
import FormControl from '@mui/material/FormControl';
import Editor from '@fuse/components/Editor';
import AddServicePriorityFromQuickAction from 'app/fuse-layouts/shared-components/modal/AddServicePriorityFromQuickAction';
import AddServiceCategoryFromQuickAction from 'app/fuse-layouts/shared-components/modal/AddServiceCategoryFromQuickAction';
import AddServiceSourceFromQuickAction from 'app/fuse-layouts/shared-components/modal/AddServiceSourceFromQuickAction';
import AddServiceStatusFromQuickAction from 'app/fuse-layouts/shared-components/modal/AddServiceStatusFromQuickAction';
import FilePreviewModal from 'helper/filePreview';
import AlertModal from 'helper/alertModal';
import { acceptFileType } from 'helper/common';
import AlphabeticalSorting from '@fuse/utils/alphabeticalSort';
import { useDebounce } from '@fuse/hooks';
import { downloadFile } from '@fuse/utils/fileUtils';

const useStyles = makeStyles((theme) => ({
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
}))


const AddTemplateFromQuickAction = ({
    handleOpenCloseAddModalEvent,
    openAddStatusModalStateData,
    newDataAddedEvent
}) => {

    const classes = useStyles()
    const dispatch = useDispatch()
    const toast = useToast(dispatch)

    const [openAlertModal, setOpenAlertModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false)
    const [uploadingFile, setUploadingFile] = useState(false)
    const [fileName, setFileName] = useState('')
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [filePreviewLink, setFilePreviewLink] = useState('')
    const [openFilePreviewLink, setOpenFilePreviewLink] = useState(false)
    const [inputKey, setInputKey] = useState(0);
    const [prioritiesMenu, setPriorityMenu] = useState([])
    const [sourcesMenu, setSourceMenu] = useState([])
    const [categoryMenu, setCategoryMenu] = useState([])
    const [statusMenu, setStatusMenu] = useState([]);
    const [openAddStatusModal, setOpenAddStatusModal] = useState(false);
    const [openAddSourceModal, setOpenAddSourceModal] = useState(false);
    const [openAddCategoryModal, setOpenAddCategoryModal] = useState(false);
    const [openAddPriorityModal, setOpenAddPriorityModal] = useState(false);

    const validationSchema = [
        yup.object({
            issue: yup.string().required('Issue is required!'),
            priority: yup.object().shape({
                id: yup.string().notRequired(),
                label: yup.string().notRequired(),
                color: yup.object().shape({
                    code: yup.string().required('Priority is required!'),
                    name: yup.string().required('Priority is required!'),
                }).nullable()
            }).required('Priority is required!'),
            source: yup.object().shape({
                id: yup.string().required('Source is required!'),
                label: yup.string().required('Source is required!'),
            }).required('Source is required!'),
            type: yup.object().shape({
                id: yup.string().notRequired(),
                label: yup.string().required('Type is required!'),
            }).required('Type is required!'),
            status: yup.object().shape({
                id: yup.string().notRequired(),
                label: yup.string().required('Status is required!'),
                color: yup.object().shape({
                    code: yup.string().notRequired(),
                    name: yup.string().notRequired(),
                }).nullable()
            }).required('Status is required!'),
            category: yup.object().shape({
                id: yup.string().required('Category is required!'),
                label: yup.string().required('Category is required!'),
            }).required('Category is required!'),
            subjectLine: yup.string()
                .trim()
                .max(30, "Subject line must be at most 30 characters!")
                .required("Subject Line is required!"),
            description: yup.string()
                .trim()
                .required()
        })
    ]


    const types = [
        {
            id: 1,
            label: 'Incident'
        },
        {
            id: 2,
            label: 'Request'
        }
    ]

    const {
        control,
        formState: { errors },
        handleSubmit,
        reset,
        setValue,
        clearErrors,
        getValues,
        trigger
    } = useForm({
        shouldUnregister: false,
        defaultValues: {
            priority: null,
            source: null,
            type: null,
            status: null,
            category: null,
            issue: '',
            subjectLine: '',
            description: ''
        },
        resolver: yupResolver(validationSchema[0]),
        mode: "onChange"
    });


    const fetchStatusSuggestions = (search = '') => {
        setIsLoading(true)
        axios.get(`/service-request-status/fetch-status-suggestion?search=${search}`).then(response => {
            const assets = AlphabeticalSorting(response.data.data, 'status_name')
            setIsLoading(false)
            const systemDefinedStatus = []; const rest = [];
            for (const item of assets) {
                const obj = { id: item.id, label: item.status_name, color: item?.status_color?.code };
                if (item.is_system_generated) systemDefinedStatus.push(obj)
                else rest.push(obj)
            }
            setStatusMenu([...systemDefinedStatus, ...rest])
        }).catch(err => {
            console.log('136=>', err);
        })
    }

    const fetchCategorySuggestions = (search = '') => {
        setIsLoading(true)
        axios.get(`/service-request-category/fetch-category-suggestion?search=${search}`).then(response => {
            const categories = AlphabeticalSorting(response.data.data, 'category_name')
            setIsLoading(false)
            const systemDefinedCategories = []; const rest = [];
            for (const item of categories) {
                const obj = { id: item.id, label: item.category_name };
                if (item.is_system_generated) systemDefinedCategories.push(obj)
                else rest.push(obj)
            }
            setCategoryMenu([...systemDefinedCategories, ...rest]);
        }).catch(err => {
            console.log('136=>', err);
        })
    }

    const fetchPrioritySuggestionsOnInputChange = useDebounce(value => {
        fetchPrioritySuggestions(value)
    }, 500);

    const fetchSourceSuggestionsOnInputChange = useDebounce(value => {
        fetchSourceSuggestions(value)
    }, 500);

    const fetchCategorySuggestionsOnInputChange = useDebounce(value => {
        fetchCategorySuggestions(value)
    }, 500);

    const fetchStatusSuggestionsOnInputChange = useDebounce(value => {
        fetchStatusSuggestions(value)
    }, 500);

    const fetchPrioritySuggestions = (search = '') => {
        setIsLoading(true)
        axios.get(`/service-request-priority/fetch-priority-suggestion?search=${search}`).then(response => {
            const priorities = AlphabeticalSorting(response.data.data, 'name')
            setIsLoading(false)
            const systemDefinedPriorities = []; const rest = [];
            for (const item of priorities) {
                const obj = { id: item.id, label: item.name, color: item.priority_color.code };
                if (item.is_system_generated) systemDefinedPriorities.push(obj)
                else rest.push(obj)
            }
            setPriorityMenu([...systemDefinedPriorities, ...rest]);
        }).catch(err => {
            console.log('136=>', err);
        })
    }

    const fetchSourceSuggestions = (search = '') => {
        setIsLoading(true)
        axios.get(`/service-request-source/fetch-source-suggestion?search=${search}`).then(response => {
            const sources = AlphabeticalSorting(response.data.data, 'source_name')
            setIsLoading(false)
            const systemDefinedSource = []; const rest = [];
            for (const item of sources) {
                const obj = { id: item.id, label: item.source_name };
                if (item.is_system_generated) systemDefinedSource.push(obj)
                else rest.push(obj)
            }
            setSourceMenu([...systemDefinedSource, ...rest]);
        }).catch(err => {
            console.log('136=>', err);
        })
    }

    useEffect(() => {
        fetchPrioritySuggestions()
        fetchSourceSuggestions()
        fetchCategorySuggestions()
        fetchStatusSuggestions()
    }, [])

    // Editor onChange Event
    function onEditorContentChange(content) {
        setValue('description', content)
        content === '' ? trigger('description') : clearErrors("description")
    }

    function handleClose() {
        // props.close('ticket')
        reset()
        setFileName('')
    }

    const handleAlertModal = (_, reason) => {
        if (reason === 'backdropClick') {
            return;
        }
        setOpenAlertModal(!openAlertModal)
    }

    const handleCloseAlertModal = () => {
        setOpenAlertModal(false)
        handleClose()
    }

    const onSubmit = async (data) => {
        const payload = {
            subject: data.subjectLine,
            description: data.description,
            issue_name: data?.issue,
            priority_id: parseInt(data?.priority?.id),
            source_id: parseInt(data?.source?.id),
            type: data?.type?.label?.toLowerCase(),
            status_id: parseInt(data?.status?.id),
            category_id: parseInt(data?.category?.id),
        }
        let result
        setIsLoading(true)
        const newFiles = selectedFiles?.filter((item) => !item?.file_link)
        const url = '/service_template/create';
        await axios.post(url, payload, {
        }).then(results => {
            result = results.data
            if (result?.statusCode === 200) {
                if (newFiles?.length === 0) {
                    toast.success(result.message)
                    newDataAddedEvent(result.data);
                    handleClose()
                }
            } else {
                toast.error(result.message)
            }
        }).catch(err => {
            toast.error(err.response.data.message);
        }).finally(() => {
            setIsLoading(false)
        });

        if (newFiles.length > 0 && result?.statusCode === 200) {
            setUploadingFile(true)
            const formData = new FormData();
            for (let i = 0; i < newFiles.length; i++) {
                formData.append('files', newFiles[i]);
            }
            formData.append('service_template_id', result.data.id);
            formData.append('isUpdate', false)

            await axios.post('/service_template/file-upload', formData).then(response => {
                if (result?.statusCode === 200) {
                    toast.success(result.message === 'No changes found' ? 'File uploaded successfully!' : result.message)
                    newDataAddedEvent(result.data);
                    handleClose()
                } else {
                    toast.error(response.data.message)
                }
            }).catch(err => {
                console.log('136=>', err);
                toast.error(err.response.data.message);
            }).finally(() => {
                setUploadingFile(false)
            })
        }
    }

    const handelChange = (event) => {
        if (selectedFiles.length >= 10) {
            return toast.error("You cannot add more than 10 file.");
        }
        const fileExtension = event.target.files[0] ? `.${event.target.files[0].name.split('.').pop().toLowerCase()}` : '';
        if (event.target.files[0] && !acceptFileType.includes(fileExtension)) {
            return toast.error(`${fileExtension} files are not allowed!`)
        }
        const selectedFilesSize = selectedFiles.reduce((acc, file) => acc + file.size, 0);
        if (event.target.files[0].size > 2000000) {
            return toast.error("File size cannot exceed 2Mb!");
        }
        if (event.target.files[0].size + selectedFilesSize > 2000000) {
            return toast.error("Total files size cannot exceed 2Mb!");
        }
        if (event.target.files[0]) {
            setSelectedFiles((prev) => [...prev, event.target.files[0]])
        }

        // Reset the key to force re-render of input
        setInputKey(prevKey => prevKey + 1);
    }
    const removeFile = (file) => {
        setSelectedFiles(selectedFiles.filter(function (item) {
            return item.name !== file.name
        }))
    }

    const handleOpenCloseAddStatus = () => {
        setOpenAddStatusModal(!openAddStatusModal)
    }

    const handleOpenCloseAddSource = () => {
        setOpenAddSourceModal(!openAddSourceModal)
    }

    const handleOpenCloseAddCategory = () => {
        setOpenAddCategoryModal(!openAddCategoryModal)
    }

    const handleOpenCloseAddPriority = () => {
        setOpenAddPriorityModal(!openAddPriorityModal)
    }


    const newStatusAddedEvent = (createdData) => {
        setOpenAddStatusModal(!openAddStatusModal)
        setValue("status", {
            id: createdData.id,
            label: createdData.status_name,
            color: createdData.status_color.code,
        })
        clearErrors("status")
        fetchStatusSuggestions()
    }

    const newSourceAddedEvent = (createdData) => {
        setOpenAddSourceModal(!openAddSourceModal)
        setValue("source", {
            id: createdData.id,
            label: createdData.source_name
        })
        clearErrors("source")
        fetchSourceSuggestions()
    }

    const newCategoryAddedEvent = (createdData) => {
        setOpenAddCategoryModal(!openAddCategoryModal)
        setValue("category", {
            id: createdData.id,
            label: createdData.category_name
        })
        clearErrors("category")
        fetchCategorySuggestions()
    }


    const newPriorityAddedEvent = (createdData) => {
        setOpenAddPriorityModal(!openAddPriorityModal)
        setValue("priority", {
            id: createdData.id,
            label: createdData.name,
            color: createdData.priority_color.code
        })
        clearErrors("priority")
        fetchPrioritySuggestions()
    }

    return (

        <>
            <Dialog
                className="cstm_dialog_modal medium text-center"
                open={openAddStatusModalStateData}
                // onClose={handleOpenCloseAddModalEvent}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                disableScrollLock
            >
                <DialogTitle id="alert-dialog-title">
                    <div className='heading_holder flexbox_holder items-center w-full'>
                        <h2 className="main_heading">Add Template</h2>
                        <Button className='transparent_icon_btn' onClick={handleOpenCloseAddModalEvent}>
                            <i className="icon ti ti-x" />
                        </Button>
                    </div>
                </DialogTitle>
                <DialogContent>
                    <div className="text-left">
                        <div className='content_holder'>
                            <Box>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Box>
                                            <Typography className='status_text'>Issue <span className='required_span'>*</span></Typography>
                                            <FormControl fullWidth className='assets_listSelect'>
                                                <Controller
                                                    name='issue'
                                                    control={control}
                                                    render={({ field }) => (
                                                        <TextField
                                                            {...field}
                                                            style={{ background: 'white' }}
                                                            variant='outlined'
                                                            placeholder='Issue'
                                                            autoFocus
                                                            fullWidth
                                                            required
                                                            error={!!errors.issue}
                                                            helperText={errors?.issue?.message}
                                                            FormHelperTextProps={{
                                                                style: {
                                                                    margin: 0,
                                                                    backgroundColor: '#f4f4f4',
                                                                    width: '100%',
                                                                    paddingTop: '2px'
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </FormControl>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box>
                                            <Typography className='status_text'>Priority <span className='required_span'>*</span></Typography>
                                            <FormControl fullWidth className='assets_listSelect'>
                                                <Controller
                                                    name="priority"
                                                    className="mb-16"
                                                    control={control}
                                                    render={({ field: { ref, ...field }, fieldState: { error } }) => (
                                                        <>
                                                            <Autocomplete
                                                                disablePortal
                                                                {...field}
                                                                style={{ width: '100%' }}
                                                                className='main_autocomplete'
                                                                value={field.value ?? null}
                                                                options={prioritiesMenu}
                                                                // defaultValue={getValues().priority?.name}
                                                                // disablePortal
                                                                PaperComponent={({ children }) => (
                                                                    <Paper className='autocomplete_holder extra_styling'>{children}
                                                                        <div className='p-16'>
                                                                            <Button className='secondary_btn w-full' onMouseDown={(event) => {
                                                                                event.stopPropagation();
                                                                                event.preventDefault();
                                                                                handleOpenCloseAddPriority()
                                                                            }}>
                                                                                <i className='ti ti-plus' />
                                                                                <span>Add Priority</span>
                                                                            </Button>
                                                                        </div>
                                                                    </Paper>
                                                                )}
                                                                onChange={(event, newValue) => field.onChange(newValue ?? null)}
                                                                getOptionLabel={option => option.label ?? ''}
                                                                renderOption={(props, option) => (
                                                                    <li {...props} key={option.id}>
                                                                        <div className="cstm_black_border_badge_holder w-full"
                                                                            style={{ borderColor: option.color, cursor: 'pointer' }}>
                                                                            <span className="dot_holder" style={{ backgroundColor: option.color }}>&nbsp;</span>
                                                                            {option.label}
                                                                        </div>
                                                                    </li>
                                                                )}
                                                                renderInput={(params) => {
                                                                    return (
                                                                        <div ref={params.InputProps.ref}>
                                                                            <TextField
                                                                                name="priority"
                                                                                style={{ background: 'white' }}
                                                                                {...params}
                                                                                // label="Status"
                                                                                placeholder='Select'
                                                                                required
                                                                                onChange={(event) => {
                                                                                    if (event && event.target) {
                                                                                        fetchPrioritySuggestionsOnInputChange(event.target.value)
                                                                                    }
                                                                                }}
                                                                                // defaultValue={defaultValues.priority?.name}
                                                                                error={!!errors.priority?.id}
                                                                                helperText={errors?.priority?.message && 'Priority is required'}
                                                                                FormHelperTextProps={{
                                                                                    style: {
                                                                                        margin: 0,
                                                                                        backgroundColor: '#f4f4f4',
                                                                                        width: '100%',
                                                                                        paddingTop: '2px',
                                                                                        fontSize: '10px',
                                                                                        color: '#FF0000'
                                                                                    }
                                                                                }}
                                                                                variant="outlined"
                                                                                fullWidth
                                                                            />
                                                                        </div>
                                                                    )
                                                                }}
                                                            />
                                                        </>
                                                    )}
                                                />
                                            </FormControl>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box>
                                            <Typography className='status_text'>Source <span className='required_span'>*</span></Typography>
                                            <FormControl fullWidth className='assets_listSelect'>
                                                <Controller
                                                    name="source"
                                                    className="mb-16"
                                                    control={control}
                                                    render={({ field: { ref, ...field }, fieldState: { error } }) => (
                                                        <>
                                                            <Autocomplete
                                                                disablePortal
                                                                {...field}
                                                                style={{ width: '100%' }}
                                                                className='main_autocomplete'
                                                                options={sourcesMenu}
                                                                value={field.value ?? null}
                                                                // defaultValue={getValues().source}
                                                                // disablePortal
                                                                PaperComponent={({ children }) => (
                                                                    <Paper className='autocomplete_holder extra_styling'>{children}
                                                                        <div className='p-16'>
                                                                            <Button className='secondary_btn w-full' onMouseDown={(event) => {
                                                                                event.stopPropagation();
                                                                                event.preventDefault();
                                                                                handleOpenCloseAddSource()
                                                                            }}>
                                                                                <i className='ti ti-plus' />
                                                                                <span>Add Source</span>
                                                                            </Button>
                                                                        </div>
                                                                    </Paper>
                                                                )}
                                                                onChange={(event, newValue) => field.onChange(newValue ?? null)}
                                                                getOptionLabel={option => option.label ?? ''}
                                                                renderInput={(params) => {
                                                                    return (
                                                                        <div ref={params.InputProps.ref}>
                                                                            <TextField
                                                                                name="source"
                                                                                style={{ background: 'white' }}
                                                                                {...params}
                                                                                // label="Status"
                                                                                placeholder='Select'
                                                                                required
                                                                                onChange={(event) => {
                                                                                    if (event && event.target) {
                                                                                        fetchSourceSuggestionsOnInputChange(event.target.value)
                                                                                    }
                                                                                }}
                                                                                // defaultValue={getValues().source}
                                                                                error={!!errors.source?.id}
                                                                                helperText={errors?.source?.message && 'Source is required'}
                                                                                FormHelperTextProps={{
                                                                                    style: {
                                                                                        margin: 0,
                                                                                        backgroundColor: '#f4f4f4',
                                                                                        width: '100%',
                                                                                        paddingTop: '2px',
                                                                                        fontSize: '10px',
                                                                                        color: '#FF0000'
                                                                                    }
                                                                                }}
                                                                                variant="outlined"
                                                                                fullWidth
                                                                            />
                                                                        </div>
                                                                    )
                                                                }}
                                                            />
                                                        </>
                                                    )}
                                                />
                                            </FormControl>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box>
                                            <Typography className='status_text'>Type <span className='required_span'>*</span></Typography>
                                            <FormControl fullWidth className='assets_listSelect'>
                                                <Controller
                                                    name="type"
                                                    className="mb-16"
                                                    control={control}
                                                    render={({ field: { ref, ...field }, fieldState: { error } }) => (
                                                        <>
                                                            <Autocomplete
                                                                disablePortal
                                                                {...field}
                                                                style={{ width: '100%' }}
                                                                className='main_autocomplete'
                                                                value={field.value ?? null}
                                                                options={types}
                                                                // defaultValue={getValues().type}
                                                                onChange={(event, newValue) => field.onChange(newValue ?? null)}
                                                                getOptionLabel={option => option.label ?? ''}
                                                                renderInput={(params) => {
                                                                    return (
                                                                        <div ref={params.InputProps.ref}>
                                                                            <TextField
                                                                                name="type"
                                                                                placeholder='Select'
                                                                                style={{ background: 'white' }}
                                                                                {...params}
                                                                                required
                                                                                // defaultValue={defaultValues.type}
                                                                                error={!!errors.type?.id}
                                                                                helperText={errors?.type?.message && 'Type is required'}
                                                                                FormHelperTextProps={{
                                                                                    style: {
                                                                                        margin: 0,
                                                                                        backgroundColor: '#f4f4f4',
                                                                                        width: '100%',
                                                                                        paddingTop: '2px',
                                                                                        fontSize: '10px',
                                                                                        color: '#FF0000'
                                                                                    }
                                                                                }}
                                                                                variant="outlined"
                                                                                fullWidth
                                                                            />
                                                                        </div>
                                                                    )
                                                                }}
                                                            />
                                                        </>
                                                    )}
                                                />
                                            </FormControl>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box>
                                            <Typography className='status_text'>Status <span className='required_span'>*</span></Typography>
                                            <FormControl fullWidth className='assets_listSelect'>
                                                <Controller
                                                    name="status"
                                                    className="mb-16"
                                                    control={control}
                                                    render={({ field: { ref, ...field }, fieldState: { error } }) => (
                                                        <>
                                                            <Autocomplete
                                                                disablePortal
                                                                {...field}
                                                                style={{ width: '100%' }}
                                                                className='main_autocomplete'
                                                                value={field.value ?? null}
                                                                options={statusMenu}
                                                                PaperComponent={({ children }) => (
                                                                    <Paper className='autocomplete_holder extra_styling'>{children}
                                                                        <div className='p-16'>
                                                                            <Button className='secondary_btn w-full' onMouseDown={(event) => {
                                                                                event.stopPropagation();
                                                                                event.preventDefault();
                                                                                handleOpenCloseAddStatus()
                                                                            }}>
                                                                                <i className='ti ti-plus' />
                                                                                <span>Add Status</span>
                                                                            </Button>
                                                                        </div>
                                                                    </Paper>
                                                                )}
                                                                onChange={(event, newValue) => field.onChange(newValue ?? null)}
                                                                getOptionLabel={option => option.label ?? ''}
                                                                renderOption={(props, option) => (
                                                                    <li {...props} key={option.id}>
                                                                        <div className="cstm_black_border_badge_holder w-full"
                                                                            style={{ borderColor: option.color, cursor: 'pointer' }}>
                                                                            <span className="dot_holder" style={{ backgroundColor: option.color }}>&nbsp;</span>
                                                                            {option.label}
                                                                        </div>
                                                                    </li>
                                                                )}
                                                                renderInput={(params) => {
                                                                    return (
                                                                        <div ref={params.InputProps.ref}>
                                                                            <TextField
                                                                                name="status"
                                                                                style={{ background: 'white' }}
                                                                                {...params}
                                                                                placeholder='Select'
                                                                                required
                                                                                onChange={(event) => {
                                                                                    if (event && event.target) {
                                                                                        fetchStatusSuggestionsOnInputChange(event.target.value)
                                                                                    }
                                                                                }}
                                                                                // defaultValue={defaultValues.status}
                                                                                error={!!errors.status?.id}
                                                                                helperText={errors?.status?.message && 'Status is required'}
                                                                                FormHelperTextProps={{
                                                                                    style: {
                                                                                        margin: 0,
                                                                                        backgroundColor: '#f4f4f4',
                                                                                        width: '100%',
                                                                                        paddingTop: '2px',
                                                                                        fontSize: '10px',
                                                                                        color: '#FF0000'
                                                                                    }
                                                                                }}
                                                                                variant="outlined"
                                                                                fullWidth
                                                                            />
                                                                        </div>
                                                                    )
                                                                }}
                                                            />
                                                        </>
                                                    )}
                                                />
                                            </FormControl>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box>
                                            <Typography className='status_text'>Category <span className='required_span'>*</span></Typography>
                                            <FormControl fullWidth className='assets_listSelect'>
                                                <Controller
                                                    name="category"
                                                    className="mb-16"
                                                    control={control}
                                                    render={({ field: { ref, ...field }, fieldState: { error } }) => (
                                                        <>
                                                            <Autocomplete
                                                                disablePortal
                                                                {...field}
                                                                style={{ width: '100%' }}
                                                                className='main_autocomplete'
                                                                value={field.value ?? null}
                                                                options={categoryMenu}
                                                                // disablePortal
                                                                PaperComponent={({ children }) => (
                                                                    <Paper className='autocomplete_holder extra_styling'>{children}
                                                                        <div className='p-16'>
                                                                            <Button className='secondary_btn w-full' onMouseDown={(event) => {
                                                                                event.stopPropagation();
                                                                                event.preventDefault();
                                                                                handleOpenCloseAddCategory()
                                                                            }}>
                                                                                <i className='ti ti-plus' />
                                                                                <span>Add Category</span>
                                                                            </Button>
                                                                        </div>
                                                                    </Paper>
                                                                )}
                                                                onChange={(event, newValue) => {
                                                                    event.preventDefault();
                                                                    event.stopPropagation();
                                                                    field.onChange(newValue ?? null)
                                                                }}
                                                                getOptionLabel={option => option.label ?? ''}
                                                                renderInput={(params) => {
                                                                    return (
                                                                        <div ref={params.InputProps.ref}>
                                                                            <TextField
                                                                                name="category"
                                                                                style={{ background: 'white' }}
                                                                                {...params}
                                                                                // label="Status"
                                                                                placeholder='Select'
                                                                                required
                                                                                onChange={(event) => {
                                                                                    if (event && event.target) {
                                                                                        fetchCategorySuggestionsOnInputChange(event.target.value)
                                                                                    }
                                                                                }}
                                                                                // defaultValue={defaultValues.category}
                                                                                error={!!errors.category?.id}
                                                                                helperText={errors?.category?.message && 'Category is required'}
                                                                                FormHelperTextProps={{
                                                                                    style: {
                                                                                        margin: 0,
                                                                                        backgroundColor: '#f4f4f4',
                                                                                        width: '100%',
                                                                                        paddingTop: '2px',
                                                                                        fontSize: '10px',
                                                                                        color: '#FF0000'
                                                                                    }
                                                                                }}
                                                                                variant="outlined"
                                                                                fullWidth
                                                                            />
                                                                        </div>
                                                                    )
                                                                }}
                                                            />
                                                        </>
                                                    )}
                                                />
                                            </FormControl>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Box>
                                            <Typography className='status_text'>
                                                Subject Line
                                                <span className='required_span'>*</span>
                                            </Typography>
                                            <Controller
                                                name="subjectLine"
                                                className='serial_numberField'
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        variant='outlined'
                                                        fullWidth
                                                        style={{ background: 'white' }}
                                                        placeholder='e.g Access Point is offline'
                                                        error={!!errors.subjectLine}
                                                        helperText={errors?.subjectLine?.message}
                                                        FormHelperTextProps={{
                                                            style: {
                                                                margin: 0,
                                                                backgroundColor: '#f4f4f4',
                                                                width: '100%',
                                                                paddingTop: '2px',
                                                                fontSize: '10px',
                                                                color: '#FF0000'
                                                            }
                                                        }}
                                                    />
                                                )}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Box>
                                            <Typography className='status_text'>Description
                                                <span className='required_span'>*</span>
                                            </Typography>
                                            <Controller
                                                name="description"
                                                className='serial_numberField'
                                                control={control}
                                                render={({ field }) => (
                                                    <Editor
                                                        editorContent={getValues('description')}
                                                        onEditorContentChange={onEditorContentChange}
                                                        placeholderText="Tell us a bit more about the issue that can help us in investigating this"
                                                    />
                                                )}
                                            />
                                            {errors?.description && <span className='required_span  mt-2'
                                                style={{
                                                    fontSize: '12px',
                                                    color: '#FF0000',
                                                }}
                                            >Description is required!</span>}
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12}>
                                        {/* Image upload */}
                                        <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '10px' }}>
                                            <Typography className='status_text'>Upload Files</Typography>
                                            <div className='upload_imgBox flex-wrap relative'>
                                                <label htmlFor="upload-photo" style={{ cursor: 'pointer', maxWidth: '200px' }}>
                                                    <span style={{ color: '#667085' }} className='icon_holder'>
                                                        <Icon>
                                                            photo_camera
                                                        </Icon>
                                                        <span className='file_info_content'><span>Upload files</span><br />Supports only png, jpg, jpeg, doc, docx, pdf, txt, xls, xlsx, csv Max size: 2Mb (rec. 700x430px)</span>
                                                    </span>
                                                </label>
                                                <input type="file"
                                                    onChange={(e) => handelChange(e)}
                                                    name="photo" id="upload-photo"
                                                    key={inputKey}
                                                />

                                            </div>
                                        </div>
                                        <div style={{ marginLeft: '10px', marginTop: '2px' }} className='w-full'>
                                            {uploadingFile && <CircularProgress style={{ textAlign: 'center' }} />}
                                            {
                                                selectedFiles.map((file, key) => (
                                                    <>
                                                        <div className='singleAttachment mt-1' key={key}>
                                                            <span className='me-3 fs-6 fw-semibold text-truncate'
                                                                onClick={() => {
                                                                    if (file.name == '' || file.file_name == 'No File Name') return;
                                                                    const fileExtension = file.name ? file.name.split('.').pop().toLocaleLowerCase() : file.file_name.split('.').pop().toLocaleLowerCase()
                                                                    const imageFileExtension = ['png', 'jpeg', 'jpg']
                                                                    if (imageFileExtension.includes(fileExtension)) {
                                                                        setOpenFilePreviewLink(true)
                                                                        setFilePreviewLink(URL.createObjectURL(file))
                                                                        setFileName(file.name ?? file?.file_name)
                                                                    } else {

                                                                        downloadFile(URL.createObjectURL(file), file.name ?? file?.file_name)
                                                                    }
                                                                }}
                                                                style={{
                                                                    width: '150px', display: 'inline-block', cursor: 'pointer'
                                                                }}>{file?.name ?? file?.file_name} </span>
                                                            <div className="file-actions">
                                                                {['png', 'jpeg', 'jpg'].includes(file?.name ? file.name.split('.').pop().toLocaleLowerCase() : file?.file_name.split('.').pop().toLocaleLowerCase()) &&
                                                                    <Tooltip id="view" title="View" placement="top">
                                                                        <i style={{ color: '#1D2939', fontSize: '18px', padding: '0 4px', cursor: 'pointer' }}
                                                                            className="ti ti-eye"
                                                                            onClick={() => {
                                                                                const fileExtension = file.name ? file.name.split('.').pop().toLocaleLowerCase() : file.file_name.split('.').pop().toLocaleLowerCase()
                                                                                const imageFileExtension = ['png', 'jpeg', 'jpg']
                                                                                if (imageFileExtension.includes(fileExtension)) {
                                                                                    setOpenFilePreviewLink(true)
                                                                                    setFilePreviewLink(file?.file_name ? file.file_link : URL.createObjectURL(file))
                                                                                    setFileName(file.name ?? file?.file_name)
                                                                                }
                                                                            }}
                                                                        />
                                                                    </Tooltip>
                                                                }
                                                                {!['png', 'jpeg', 'jpg'].includes(file?.name ? file.name.split('.').pop().toLocaleLowerCase() : file?.file_name.split('.').pop().toLocaleLowerCase()) &&
                                                                    <i
                                                                        className='ti ti-download'
                                                                        style={{ color: '#1D2939', fontSize: '18px', padding: '0 4px', cursor: 'pointer' }}
                                                                        onClick={() => {
                                                                            const fileExtension = file.name ? file.name.split('.').pop().toLocaleLowerCase() : file.file_name.split('.').pop().toLocaleLowerCase()
                                                                            const imageFileExtension = ['png', 'jpeg', 'jpg']
                                                                            if (!imageFileExtension.includes(fileExtension)) {
                                                                                downloadFile(file.file_name ? file.file_link : URL.createObjectURL(file), file.name ?? file?.file_name)
                                                                            }
                                                                        }}
                                                                    />
                                                                }

                                                                {!file?.file_name &&
                                                                    <i className='ti ti-xbox-x' onClick={(e) => {
                                                                        removeFile(file)
                                                                }} />
                                                                }
                                                            </div>

                                                        </div >
                                                    </>
                                                ))
                                            }
                                        </div>
                                    </Grid>
                                </Grid>
                            </Box>
                        </div>
                    </div>
                </DialogContent>
                <DialogActions className="action_footer_holder">
                    <Button onClick={handleOpenCloseAddModalEvent} className="secondary_btn">Cancel</Button>
                    <Button onClick={handleSubmit(onSubmit)} disabled={isLoading || uploadingFile} className="primary_btn" style={{  marginRight: '16px'}}>
                        Save and Select
                    </Button>
                </DialogActions>
            </Dialog>
            {openAlertModal &&
                <AlertModal
                    openAlerModal={openAlertModal}
                    module="service Ticket"
                action="adding"
                    handleCloseAlertModal={handleCloseAlertModal}
                    handleAlertModal={handleAlertModal}
                />
            }


            {
                openFilePreviewLink && <FilePreviewModal
                    open={openFilePreviewLink}
                    close={() => setOpenFilePreviewLink(false)}
                    setOpen={setOpenFilePreviewLink}
                    filePreviewLink={filePreviewLink}
                    fileName={fileName}
                />
            }

            {
                openAddStatusModal &&
                <AddServiceStatusFromQuickAction
                    handleOpenCloseAddModalEvent={handleOpenCloseAddStatus}
                    openAddStatusModalStateData={openAddStatusModal}
                    newDataAddedEvent={newStatusAddedEvent}
                />
            }

            {
                openAddSourceModal &&
                <AddServiceSourceFromQuickAction
                    handleOpenCloseAddModalEvent={handleOpenCloseAddSource}
                    openAddSourceModalStateData={openAddSourceModal}
                    newDataAddedEvent={newSourceAddedEvent}
                />
            }

            {
                openAddCategoryModal &&
                <AddServiceCategoryFromQuickAction
                    handleOpenCloseAddModalEvent={handleOpenCloseAddCategory}
                    openAddCategoryModalStateData={openAddCategoryModal}
                    newDataAddedEvent={newCategoryAddedEvent}
                />
            }

            {
                openAddPriorityModal &&
                <AddServicePriorityFromQuickAction
                    handleOpenCloseAddModalEvent={handleOpenCloseAddPriority}
                    openAddPriorityModalStateData={openAddPriorityModal}
                    newDataAddedEvent={newPriorityAddedEvent}
                />
            }
        </>

    );
}

export default AddTemplateFromQuickAction